import { COMMAND_CENTER_ROOT } from './commandCenterRoutes'
import { useParams, useNavigate } from 'react-router-dom'
import { ChannelView, useChannels, useDms, useChannelMessages, useTeamTasks, createTask, mutateTask, COMMS_ROOT, TEAM_ROOT, CONFERENCE_CALLS_ROOT, type Message } from './comms'
import { createConferenceCall, conferenceRoomPath } from './comms/conferenceCallsData'

/**
 * V2 Channel / DM conversation — route wrapper.
 *   /square/command-center/communications/channels/:id
 *   /square/command-center/communications/dms/:id   (isDm)
 *
 * Owns the message hook + rail hooks; posting a message and creating a
 * task/blocker from the channel are real, audited saves. No seeded history.
 *
 * PHASE_2M D2: per-MESSAGE task/blocker actions create real sq_team_tasks
 * rows carrying source_message_id + source_channel_id; the context panel
 * lists the real tasks whose source_channel_id is this channel.
 */
export default function SquareCodeChannelRoute({ isDm = false }: { isDm?: boolean }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const channels = useChannels()
  const dms = useDms()
  const msgs = useChannelMessages(id)
  const tasks = useTeamTasks()
  const list = isDm ? dms.items : channels.items
  const channel = list.find((c) => c.id === id) ?? (isDm ? dms.items.find((c) => c.id === id) : channels.items.find((c) => c.id === id))

  const openTaskFromChannel = async (blocked: boolean) => {
    if (!id) return
    const task = await createTask({ title: `${blocked ? 'Blocker' : 'Task'} from ${channel?.name || 'channel'}`, source_channel_id: id })
    if (blocked) { try { await mutateTask('set_blocker', { id: task.id, blocker_reason: `Flagged from ${channel?.name || 'channel'}` }) } catch { /* keep the task even if the blocker set fails */ } }
    navigate(`${TEAM_ROOT}/tasks/${task.id}`)
  }

  const taskFromMessage = async (msg: Message, blocked: boolean) => {
    if (!id) return
    const title = msg.body.length > 120 ? `${msg.body.slice(0, 120)}…` : msg.body
    const task = await createTask({ title: title || 'Task from message', source_channel_id: id, source_message_id: msg.id })
    if (blocked) { try { await mutateTask('set_blocker', { id: task.id, blocker_reason: `Flagged from a message in ${channel?.name || 'channel'}` }) } catch { /* task persists regardless */ } }
    tasks.reload()
    navigate(`${TEAM_ROOT}/tasks/${task.id}`)
  }

  // Start a REAL controlled Daily room linked to this channel (context_type:
  // 'comms_channel', context_id: this channel's id) so the Conference Room's
  // Chat tab has a real channel to bind to — the create route + this linkage
  // already existed and worked; nothing here previously called it, so every
  // call created this way silently had no linked channel.
  const startConferenceCall = async () => {
    if (!id) { navigate(CONFERENCE_CALLS_ROOT); return }
    const result = await createConferenceCall({
      title: channel?.name ? `${channel.name} call` : 'Channel call',
      meeting_mode: 'internal_operator',
      channel_id: id,
    })
    if (!result.ok) { navigate(CONFERENCE_CALLS_ROOT); return }
    navigate(conferenceRoomPath(result.meeting.id), {
      state: {
        title: result.meeting.title,
        meetingMode: result.meeting.meeting_mode,
        roomName: result.meeting.daily_room_name,
        channelId: id,
      },
    })
  }

  return (
    <ChannelView
      channel={channel}
      channels={channels.items}
      dms={dms.items}
      messages={msgs.messages}
      loading={msgs.loading}
      error={msgs.error}
      authRequired={msgs.authRequired || channels.authRequired}
      railLoading={channels.loading || dms.loading}
      railError={channels.error || dms.error}
      railAuthRequired={channels.authRequired || dms.authRequired}
      isDm={isDm}
      channelTasks={tasks.items.filter((t) => t.source_channel_id === id)}
      onSend={async (body) => { await msgs.send(body) }}
      onCreateTask={() => openTaskFromChannel(false)}
      onFlagBlocker={() => openTaskFromChannel(true)}
      onCreateTaskFromMessage={(msg) => taskFromMessage(msg, false)}
      onFlagBlockerFromMessage={(msg) => taskFromMessage(msg, true)}
      onOpenTask={(taskId) => navigate(`${TEAM_ROOT}/tasks/${taskId}`)}
      onStartConferenceCall={() => { void startConferenceCall() }}
      onOpenChannel={(cid) => navigate(`${COMMS_ROOT}/channels/${cid}`)}
      onOpenDm={(cid) => navigate(`${COMMS_ROOT}/dms/${cid}`)}
      onOpenTeam={() => navigate(TEAM_ROOT)}
      onOpenConferenceCalls={() => navigate(CONFERENCE_CALLS_ROOT)}
      onOpenTasks={() => navigate(TEAM_ROOT)}
      onHome={() => navigate(COMMAND_CENTER_ROOT)}
      onBackCommandCenter={() => navigate(COMMAND_CENTER_ROOT)}
      onNavTop={(to) => navigate(to)}
    />
  )
}
